import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Bell, LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../hooks/useNotifications';

export const REQUEST_PARENT_CONSENT_PATH = '/rower/request-parent-consent';
export const ROWER_CONSENT_NOTIFICATIONS_PATH = '/rower/notifications';

interface Props {
  children?: React.ReactNode;
  /** When true, show only the blurred shell (no form) while guardianship status loads. */
  loading?: boolean;
}

/**
 * Full-viewport lockdown shell for minors who need parental (re-)consent.
 * Covers the app chrome with a blurred backdrop. Functional controls sit as
 * floating actions — no top banner. Only the request-consent form, notifications,
 * and sign-out remain reachable.
 */
const RowerConsentLockdown: React.FC<Props> = ({ children, loading = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const isNotifications = location.pathname === ROWER_CONSENT_NOTIFICATIONS_PATH;
  const isRequestPage = location.pathname === REQUEST_PARENT_CONSENT_PATH;

  const { unreadCount } = useNotifications('rower');

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-black/40 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rower-consent-lockdown-title"
    >
      <h2 id="rower-consent-lockdown-title" className="sr-only">
        Guardian consent required
      </h2>

      {!loading && (
        <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-end p-4">
          <div className="pointer-events-auto flex items-center gap-1 rounded-xl border border-gray-200 bg-white p-1 shadow-sm">
            <nav className="flex items-center gap-0.5 text-sm" aria-label="Consent lockdown">
              <Link
                to={REQUEST_PARENT_CONSENT_PATH}
                className={`rounded-lg px-3 py-1.5 transition-colors ${
                  isRequestPage
                    ? 'bg-gray-100 font-medium text-gray-900'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                Request consent
              </Link>
              <Link
                to={ROWER_CONSENT_NOTIFICATIONS_PATH}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 transition-colors ${
                  isNotifications
                    ? 'bg-gray-100 font-medium text-gray-900'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Bell className="h-3.5 w-3.5" aria-hidden />
                Notifications
                {unreadCount > 0 && (
                  <span className="ml-0.5 rounded-full bg-accent px-1.5 py-0.5 text-[10px] font-semibold leading-none text-accent-foreground">
                    {unreadCount}
                  </span>
                )}
              </Link>
            </nav>
            <button
              type="button"
              onClick={handleSignOut}
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
            >
              <LogOut className="h-3.5 w-3.5" aria-hidden />
              Sign Out
            </button>
          </div>
        </div>
      )}

      <div
        className={`min-h-0 flex-1 overflow-y-auto ${
          loading
            ? 'flex items-center justify-center p-4'
            : isNotifications
              ? 'p-4 pt-16 sm:p-6 sm:pt-16'
              : 'flex items-center justify-center p-4 pt-16 sm:p-6 sm:pt-16'
        }`}
      >
        {loading ? (
          <div className="text-center">
            <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-b-2 border-accent" />
            <p className="text-sm text-white">Checking account access...</p>
          </div>
        ) : (
          <div className={isNotifications ? 'mx-auto w-full max-w-3xl' : 'w-full max-w-lg'}>
            {children}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default RowerConsentLockdown;
